import type { ChapterDetail, Project } from "./types.js";

export type ManuscriptFormat = "txt" | "md";
type ExportChapter = Pick<ChapterDetail, "number" | "title" | "status" | "draft" | "finalText">;
const singleLine = (value: string): string => value.replace(/[\r\n\t]+/g, " ").trim();
const heading = (value: string): string => singleLine(value).replace(/[\\`*_{}\[\]<>#!|]/g, "\\$&");

export function manuscriptFilename(title: string, format: ManuscriptFormat): string {
  const base = singleLine(title).replace(/[<>:"/\\|?*\u0000-\u001f\u007f]/g, "_").replace(/[. ]+$/g, "").slice(0, 100) || "小说稿件";
  return `${base}-稿件.${format}`;
}

/** Pure formatting. This function never reads storage or changes a saved chapter. */
export function renderManuscript(project: Pick<Project, "title" | "author">, chapters: ExportChapter[], format: ManuscriptFormat): string {
  if (!chapters.length) throw new Error("所选范围内没有可导出的正文");
  const ordered = [...chapters].sort((a, b) => a.number - b.number);
  if (new Set(ordered.map((chapter) => chapter.number)).size !== ordered.length) throw new Error("章节重复，导出已取消");
  const draftCount = ordered.filter((chapter) => chapter.status !== "final").length;
  const title = singleLine(project.title);
  const author = singleLine(project.author || "未署名");
  const info = `作者：${author}\n共 ${ordered.length} 章${draftCount ? `，含 ${draftCount} 章草稿或待复审正文（非定稿）` : "，均为已定稿正文"}\n仅包含导出时读取的已保存内容，不包含编辑区尚未保存的修改。`;
  const sections = ordered.map((chapter) => {
    const body = (chapter.status === "final" ? chapter.finalText : chapter.draft || chapter.finalText).replace(/\r\n?/g, "\n").trim();
    if (!body) throw new Error(`第 ${chapter.number} 章暂无正文，可能已被删除或正在重写；未生成不完整文件，请刷新后重试`);
    const label = `第 ${chapter.number} 章 ${singleLine(chapter.title)}${chapter.status !== "final" ? " 【草稿／待复审】" : ""}`;
    return `${format === "md" ? "## " + heading(label) : label}\n\n${body}`;
  });
  return `${format === "md" ? "# " + heading(title) : title}\n\n${info}\n\n${sections.join("\n\n\n")}\n`;
}
